import { useEffect, useRef, useCallback, useState } from 'react'
import { getWsUrl } from '../constants/config'
import type { ConversationTurn, LiveFrame, NativeIR, NativeLine } from '../api/client'

// ─── Types ─────────────────────────────────────────────────────────────────────

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error'
export type AuthState = 'pending' | 'authenticated' | 'failed'

export interface UseWebSocketReturn {
  connectionState: ConnectionState
  authState: AuthState
  attachChat: (chatId: string, cols: number, rows: number) => void
  detachChat: () => void
  sendInput: (data: string) => void
  /** Submit a whole composed message to a specific chat (paste-safe on the server: body
   *  then a separate Enter). chatId travels with the message so it routes correctly even
   *  if the socket re-attached to another chat. */
  submitMessage: (chatId: string, text: string) => void
  /** Scroll the tmux pane history by N lines (>0 = up/older). Used by mobile touch-swipe. */
  scrollTerminal: (lines: number) => void
  sendResize: (cols: number, rows: number) => void
  onOutput: (cb: (data: string, chatId: string) => void) => void
  onHistory: (cb: (lines: string[], chatId: string) => void) => void
  onChatState: (cb: (chatId: string, state: string) => void) => void
  onSessionIdCaptured: (cb: (chatId: string, sessionId: string, agentPlugin: string) => void) => void
  onStructuredTurn: (cb: (turn: ConversationTurn, chatId: string) => void) => void
  onInputDropped: (cb: (chatId: string) => void) => void
  /** Ask the server to re-emit text correction (fix garbled / misrendered screen). */
  fixText: () => void
  onAgentActivity: (cb: (chatId: string, active: boolean) => void) => void
  /** WhimerTerm/WhimerClean: the recognized live frame (volatile bottom) changed. */
  onWhimerFrame: (cb: (frame: LiveFrame, chatId: string) => void) => void
  /** WhimerTerm: newly-settled committed lines streamed from the terminal (reset=replace). */
  onWhimerCommit: (cb: (lines: string[], reset: boolean, chatId: string) => void) => void
  /** WhimerTerm² (native v2): the assembled live IR (volatile bottom) changed. */
  onNativeFrame: (cb: (ir: NativeIR, chatId: string) => void) => void
  /** WhimerTerm² (native v2): newly-settled committed lines as styled segments (reset=replace). */
  onNativeCommit: (cb: (lines: NativeLine[], reset: boolean, chatId: string, opts?: { pending?: boolean; promote?: boolean }) => void) => void
  /** Chat name was auto-updated from the agent's JSONL ai-title entry. */
  onChatNameUpdated: (cb: (chatId: string, name: string) => void) => void
  /** pane.state (spec/CHAT_API.md): reactive map chatId → agent state, updated by the GLOBAL
   *  `pane_state` WS message. One source read by BOTH the chat-list badge and the typing
   *  indicator (the hook is created once in AppShell and passed down). */
  paneStates: Record<string, PaneStateInfo>
}

/** Announced agent state for a chat (spec/CHAT_API.md §1). */
export interface PaneStateInfo {
  state: 'running' | 'waiting' | 'idle' | 'error'
  since: number
  detail?: unknown
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useWebSocket(token: string | null): UseWebSocketReturn {
  const wsRef               = useRef<WebSocket | null>(null)
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected')
  const [authState, setAuthState]             = useState<AuthState>('pending')
  // pane.state (spec/CHAT_API.md): reactive map kept in sync by the GLOBAL `pane_state` message.
  const [paneStates, setPaneStates]           = useState<Record<string, PaneStateInfo>>({})
  const reconnectTimer      = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttempts   = useRef(0)
  // True between the effect's cleanup and its next run (unmount, logout, token swap).
  // ws.onclose fires AFTER cleanup (async), so without this flag the close handler of the
  // socket we just closed schedules a fresh reconnect that nothing can cancel — a zombie
  // socket per unmount, and after logout it re-authenticates with the discarded token.
  const disposed            = useRef(false)
  // Set when the server rejects our token (auth_error). A rejected token will keep being
  // rejected, so onclose must NOT schedule a reconnect — otherwise a single expired token
  // becomes an endless 1s reconnect storm. Cleared when a fresh token arrives (re-login).
  const authFailed          = useRef(false)
  const outputHandlers           = useRef<((data: string, chatId: string) => void)[]>([])
  const historyHandlers          = useRef<((lines: string[], chatId: string) => void)[]>([])
  const chatStateHandlers        = useRef<((chatId: string, state: string) => void)[]>([])
  const sessionCaptureHandlers   = useRef<((chatId: string, sessionId: string, agentPlugin: string) => void)[]>([])
  const structuredTurnHandlers   = useRef<((turn: ConversationTurn, chatId: string) => void)[]>([])
  const inputDroppedHandlers     = useRef<((chatId: string) => void)[]>([])
  const agentActivityHandlers    = useRef<((chatId: string, active: boolean) => void)[]>([])
  const whimerFrameHandlers      = useRef<((frame: LiveFrame, chatId: string) => void)[]>([])
  const whimerCommitHandlers     = useRef<((lines: string[], reset: boolean, chatId: string) => void)[]>([])
  const nativeFrameHandlers      = useRef<((ir: NativeIR, chatId: string) => void)[]>([])
  const nativeCommitHandlers     = useRef<((lines: NativeLine[], reset: boolean, chatId: string, opts?: { pending?: boolean; promote?: boolean }) => void)[]>([])
  const chatNameUpdatedHandlers  = useRef<((chatId: string, name: string) => void)[]>([])

  const connect = useCallback(() => {
    if (!token) return
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) return

    setConnectionState('connecting')
    const ws = new WebSocket(getWsUrl())
    wsRef.current = ws

    ws.onopen = () => {
      setConnectionState('connected')
      // Do NOT reset reconnectAttempts here. A socket that opens then closes right away
      // (server rejects auth, instantly drops the conn) would reset the backoff every
      // cycle, defeating it entirely and hammering at a fixed ~1s. Reset only on a
      // CONFIRMED successful auth — see the 'auth_ok' case below.
      ws.send(JSON.stringify({ type: 'auth', token }))
    }

    ws.onmessage = (event) => {
      // Stale socket (cleanup ran, or a newer socket replaced this one) — its late events
      // must not double-fire handlers or clobber the current socket's state.
      if (disposed.current || wsRef.current !== ws) return
      let msg: { type: string; [key: string]: unknown }
      try { msg = JSON.parse(event.data as string) } catch { return }

      switch (msg.type) {
        case 'auth_ok':
          setAuthState('authenticated')
          reconnectAttempts.current = 0   // confirmed good — now it's safe to reset the backoff
          break
        case 'auth_error':
          // Permanent failure for THIS token (expired / invalid / user gone). Flag it so
          // onclose stops reconnecting; the app routes to /login on authState==='failed'.
          setAuthState('failed')
          authFailed.current = true
          ws.close()
          break
        case 'output':
          outputHandlers.current.forEach(h =>
            h((msg.payload as { data: string }).data, msg.chatId as string),
          )
          break
        case 'clean_output':
          // clean_output is ANSI-stripped text for TTS / search — NOT for xterm
          // xterm already received the raw `output` message above
          // Future: wire to ttsHandlers here
          break
        case 'history':
          historyHandlers.current.forEach(h =>
            h(msg.lines as string[], msg.chatId as string),
          )
          break
        case 'chat_state':
          chatStateHandlers.current.forEach(h =>
            h(msg.chatId as string, msg.state as string),
          )
          break
        case 'session_id_captured':
          sessionCaptureHandlers.current.forEach(h =>
            h(msg.chatId as string, msg.sessionId as string, msg.agentPlugin as string),
          )
          break
        case 'structured_turn':
          structuredTurnHandlers.current.forEach(h =>
            h(msg.turn as ConversationTurn, msg.chatId as string),
          )
          break
        case 'input_dropped':
          inputDroppedHandlers.current.forEach(h => h(msg.chatId as string))
          break
        case 'agent_activity':
          agentActivityHandlers.current.forEach(h =>
            h(msg.chatId as string, msg.active as boolean),
          )
          break
        case 'whimer_frame':
          whimerFrameHandlers.current.forEach(h =>
            h(msg.frame as LiveFrame, msg.chatId as string),
          )
          break
        case 'whimer_commit':
          whimerCommitHandlers.current.forEach(h =>
            h(msg.lines as string[], !!msg.reset, msg.chatId as string),
          )
          break
        case 'native_frame':
          nativeFrameHandlers.current.forEach(h =>
            h(msg.ir as NativeIR, msg.chatId as string),
          )
          break
        case 'native_commit':
          nativeCommitHandlers.current.forEach(h =>
            h(msg.lines as NativeLine[], !!msg.reset, msg.chatId as string,
              { pending: !!msg.pending, promote: !!msg.promote }),
          )
          break
        case 'chat_name_updated':
          chatNameUpdatedHandlers.current.forEach(h =>
            h(msg.chatId as string, msg.name as string),
          )
          break
        case 'pane_state':
          // Global broadcast — update the reactive map for whichever chat it names (may be a
          // chat this client hasn't opened, which is the point: the chat list shows all).
          setPaneStates(prev => ({
            ...prev,
            [msg.chatId as string]: {
              state: msg.state as PaneStateInfo['state'],
              since: msg.since as number,
              detail: msg.detail,
            },
          }))
          break
        case 'attached':
          // chat attached — history will follow
          break
        case 'pong':
          break
        case 'error':
          console.error('[WS] Server error:', msg.message)
          break
      }
    }

    ws.onclose = () => {
      // Disposed (unmount/logout/token swap) or superseded by a newer socket: do nothing.
      // Scheduling here is exactly the leak the disposed flag exists to prevent.
      if (disposed.current || wsRef.current !== ws) return
      setConnectionState('disconnected')
      // Token was rejected — reconnecting replays the same dead token, so STOP. Stay
      // 'failed' (the app reacts by clearing the token and routing to login). A fresh
      // token re-arms via the effect below.
      if (authFailed.current) {
        setAuthState('failed')
        return
      }
      setAuthState('pending')
      // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000)
      reconnectAttempts.current++
      reconnectTimer.current = setTimeout(connect, delay)
    }

    ws.onerror = () => {
      if (disposed.current || wsRef.current !== ws) return
      setConnectionState('error')
    }
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) {
      disposed.current = false     // re-arm (fresh mount or re-login after a disposed cleanup)
      authFailed.current = false   // fresh token (re-login) → a prior rejection must not block it
      connect()
    }
    return () => {
      disposed.current = true      // must precede close(): onclose fires after this cleanup
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      wsRef.current?.close()
    }
  }, [token, connect])

  // Composed messages buffered because they were fired while the socket was down (a submit
  // during a reconnect blip). Each carries its target chatId so a flush can NEVER deliver
  // it to the wrong chat — important because one socket may rapidly re-attach between
  // chats, and we only flush a buffered submit when ITS chat is the one being attached.
  const outboundQueue = useRef<{ chatId: string; msg: object }[]>([])

  const send = useCallback((msg: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg))
    }
  }, [])

  return {
    connectionState,
    authState,
    attachChat: (chatId, cols, rows) => {
      send({ type: 'attach', chatId, cols, rows })
      // Flush only submits intended for THIS chat (don't misroute to whatever chat the
      // socket happens to be attached to now).
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const keep: { chatId: string; msg: object }[] = []
        for (const item of outboundQueue.current) {
          if (item.chatId === chatId) wsRef.current.send(JSON.stringify(item.msg))
          else keep.push(item)
        }
        outboundQueue.current = keep
      }
    },
    detachChat: () => send({ type: 'detach' }),
    sendInput: (data) => send({ type: 'input', data }),
    // Carry the chatId on the submit itself so the server routes it to the right chat
    // regardless of which chat this connection last attached (a single socket can be
    // re-attached between chats faster than a submit round-trips). Buffer + replay if
    // the socket is momentarily down.
    submitMessage: (chatId, text) => {
      const msg = { type: 'submit', chatId, data: text }
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(msg))
      } else {
        outboundQueue.current.push({ chatId, msg })
      }
    },
    scrollTerminal: (lines) => send({ type: 'scroll', lines }),
    sendResize: (cols, rows) => send({ type: 'resize', cols, rows }),
    onOutput:             (cb) => { outputHandlers.current          = [cb] },
    onHistory:            (cb) => { historyHandlers.current         = [cb] },
    onChatState:          (cb) => { chatStateHandlers.current       = [cb] },
    onSessionIdCaptured:  (cb) => { sessionCaptureHandlers.current  = [cb] },
    onStructuredTurn:     (cb) => { structuredTurnHandlers.current  = [cb] },
    onInputDropped:       (cb) => { inputDroppedHandlers.current    = [cb] },
    fixText:      () => send({ type: 'fix_text' }),
    onAgentActivity:      (cb) => { agentActivityHandlers.current   = [cb] },
    onWhimerFrame:        (cb) => { whimerFrameHandlers.current     = [cb] },
    onWhimerCommit:       (cb) => { whimerCommitHandlers.current    = [cb] },
    onNativeFrame:        (cb) => { nativeFrameHandlers.current     = [cb] },
    onNativeCommit:       (cb) => { nativeCommitHandlers.current    = [cb] },
    onChatNameUpdated:    (cb) => { chatNameUpdatedHandlers.current = [cb] },
    paneStates,
  }
}
